import { useEffect, useState } from "react";
import { FaStickyNote, FaTimes } from "react-icons/fa";
import { apiFetch } from "./api";

function formatRelative(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 60) {
      return `${diffMinutes || 1} min ago`;
    }
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hr${diffHours === 1 ? "" : "s"} ago`;
    }
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  } catch {
    return "";
  }
}

export default function PracticeNoteModal({ practiceId, onClose, onAdded }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function loadNotes() {
    if (!practiceId) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/practices/${practiceId}/notes`);
      setNotes(data?.items || []);
    } catch (e) {
      console.error("Failed to load practice notes", e);
      setError(e?.message || "Failed to load notes");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  if (!practiceId) return null;

  async function submitNote() {
    const trimmed = note.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError("");
    try {
      await apiFetch(`/practices/${practiceId}/notes`, {
        method: "POST",
        body: JSON.stringify({ note: trimmed }),
      });
      setNote("");
      await loadNotes();
      if (onAdded) onAdded();
    } catch (e) {
      setError(e?.message || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modalOverlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20, 24, 33, 0.5)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="modalCard"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 500,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg-elevated)",
          color: "var(--text)",
          borderRadius: 18,
          boxShadow: "var(--shadow-elevated)",
          border: "1px solid var(--card-border)",
        }}
      >
        <div className="modalCardContent" style={{ padding: 24 }}>
          <div className="modalHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: "var(--primary-600)",
                  color: "#e0eaff",
                  flexShrink: 0,
                }}
              >
                <FaStickyNote size={16} />
              </span>
              <h3 className="h2" style={{ margin: 0 }}>Practice Notes</h3>
            </div>
            <button
              className="modal-close"
              aria-label="Close"
              onClick={onClose}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                background: "rgba(20,35,59,0.07)",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                color: "var(--muted)",
                flexShrink: 0,
              }}
            >
              <FaTimes size={14} />
            </button>
          </div>

          <p className="help" style={{ margin: "0 0 12px 0" }}>
            Notes are posted to the Slack channel(s) mapped to this practice's team.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            <textarea
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note for this practice..."
              rows={3}
              style={{ width: "100%", resize: "vertical" }}
            />
            <button
              className="btn"
              onClick={submitNote}
              disabled={submitting || !note.trim()}
              style={{ alignSelf: "flex-end" }}
            >
              {submitting ? "Posting…" : "Post Note"}
            </button>
          </div>

          {error && (
            <p className="help" style={{ color: "var(--danger)", marginBottom: 12 }}>{error}</p>
          )}

          {loading ? (
            <p className="help" style={{ textAlign: "center", padding: 20 }}>Loading notes...</p>
          ) : notes.length === 0 ? (
            <p className="help" style={{ textAlign: "center", padding: 12 }}>No notes yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {notes.map((n) => (
                <div
                  key={n.id}
                  style={{
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    padding: 12,
                  }}
                >
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 15 }}>{n.note}</div>
                  <div className="help" style={{ marginTop: 6, display: "flex", justifyContent: "space-between" }}>
                    <span>{n.createdByName || "Coach"} · {formatRelative(n.createdAt)}</span>
                    {n.slackError && <span style={{ color: "var(--danger)" }}>Slack delivery failed</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
